import { IconBin } from '@cimpress-technology/react-streamline-icons';
import { Button, CodeBlock, Modal, ReactTablev6, Tooltip } from '@cimpress/react-components';
import { AuthorizationCodeGrant } from '@cimpress/simple-auth-wrapper';
import React, { useEffect, useState } from 'react';
import { deleteSubscription, getSubscriptions } from '../../services/subscriptions.ts';
import './subscription.css';

type Columns = {
  Header: string;
  accessor?: string | any;
  Cell?: ({ original }: { original: any }) => any;
  maxWidth?: number;
  id?: string;
}[];

type resourceFilter = {
  path: string;
  value: string;
  operator: string;
};

type SubscriptionType = {
  id: string;
  created: string;
  version: number;
  alertInfo: {
    channel: string;
    details: {
      email?: string;
      httpEndpoint?: string;
    };
  };
  resourceId: string;
  subscriber: string;
  resourceType: string;
  resourceFilters: resourceFilter[];
};

const Subscription: React.FC = () => {
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionType>();
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const removeSubscription = async (subscriptionId: string): Promise<void> => {
    try {
      await deleteSubscription(subscriptionId);
      setSubscriptionData(subscriptionData.filter(({ id }) => id !== subscriptionId));
    } catch (error) {
      console.error(error);
    }
  };

  const columns: Columns = [
    {
      Header: 'Resource Type',
      accessor: 'resourceType',
      maxWidth: 200,
      Cell: ({ original }) => (
        <Tooltip contents={original.resourceType} tooltipInnerStyle={{ minWidth: 'fit-content' }}>
          <div>{original.resourceType}</div>
        </Tooltip>
      ),
    },
    {
      Header: 'Action Name',
      accessor: 'actionName',
      maxWidth: 150,
      Cell: ({ original }) => (
        <Tooltip contents={original.actionName} tooltipInnerStyle={{ minWidth: 'fit-content' }}>
          <div>{original.actionName}</div>
        </Tooltip>
      ),
    },
    {
      Header: 'Resource Id',
      accessor: 'resourceId',
      maxWidth: 400,
      Cell: ({ original }) => (
        <Tooltip contents={original.resourceId} tooltipInnerStyle={{ minWidth: 'fit-content' }}>
          <div>{original.resourceId}</div>
        </Tooltip>
      ),
    },
    {
      Header: 'Filter',
      accessor: 'resourceFilters',
      Cell: ({ original }) => (
        <CodeBlock code={JSON.stringify(original.resourceFilters, null, 2)} />
      ),
    },
    {
      Header: 'Metadata',
      accessor: 'metadata',
      Cell: ({ original }) => (
        <CodeBlock code={JSON.stringify(original?.metadata || [], null, 2)} />
      ),
    },
    {
      id: 'chanel',
      Header: 'Channel',
      maxWidth: 100,
      accessor: (data: {
        alertInfo: { details: { email: string; httpEndpoint: string }; channel: string };
      }) =>
        `(${data.alertInfo.channel}) ${data.alertInfo.details.email || data.alertInfo.details.httpEndpoint}`,
      Cell: ({ original }) => (
        <Tooltip
          contents={`(${original.alertInfo.channel}) ${original.alertInfo.details.email || original.alertInfo.details.httpEndpoint}`}
          tooltipInnerStyle={{ minWidth: 'fit-content' }}
        >
          <div>{`(${original.alertInfo.channel}) ${original.alertInfo.details.email || original.alertInfo.details.httpEndpoint}`}</div>
        </Tooltip>
      ),
    },
    {
      Header: 'Subscriber',
      accessor: 'subscriber',
      maxWidth: 100,
      Cell: ({ original }) => (
        <Tooltip contents={original.subscriber} tooltipInnerStyle={{ minWidth: 'fit-content' }}>
          <div>{original.subscriber}</div>
        </Tooltip>
      ),
    },
    {
      Header: 'Created By',
      accessor: 'createdBy',
      maxWidth: 100,
      Cell: ({ original }) => (
        <Tooltip contents={original.createdBy} tooltipInnerStyle={{ minWidth: 'fit-content' }}>
          <div>{original.createdBy}</div>
        </Tooltip>
      ),
    },
    {
      Header: 'Action',
      maxWidth: 100,
      Cell: ({ original }) => (
        <>
          <Button
            variant="anchor"
            onClick={() => {
              setSelectedSubscription(original);
              setOpenModal(true);
            }}
          >
            <Tooltip contents={'Unsubscribe'}>
              <IconBin size={'lg'} weight={'fill'} />
            </Tooltip>
          </Button>
        </>
      ),
    },
  ];

  const fetchSubscriptions = async (): Promise<void> => {
    try {
      setLoadingData(true);
      const { subscriptions } = await getSubscriptions();

      setSubscriptionData(subscriptions);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingData(false);
    }
  };

  const refreshUserToken = (): void => {
    const authorizationCodeGrant = new AuthorizationCodeGrant({
      clientID: import.meta.env.VITE_NOTIFICATIONS_CLIENT_ID,
      redirectRoute: '/dashboard',
    });

    authorizationCodeGrant.login({ nextUri: `${import.meta.env.VITE_BASE_URL}/dashboard` });
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  return (
    <>
      <Modal
        status={'danger'}
        show={openModal}
        onRequestHide={() => setOpenModal(false)}
        closeButton={true}
        title={'Delete Subscription'}
        footer={
          <div className="" style={{ justifyContent: 'space-between' }}>
            <Button onClick={() => setOpenModal(false)} style={{ marginRight: '32px' }}>
              Close
            </Button>
            <Button
              variant={'primary'}
              onClick={() => {
                removeSubscription(selectedSubscription.id);
                setOpenModal(false);
              }}
            >
              {'Delete'}
            </Button>
          </div>
        }
      >
        <p>Deleting this subscription will permanently remove it and cannot be retrieved.</p>
      </Modal>
      <div className="flex-col">
        <div className="flex-row">
          <h1 style={{ margin: '16px 0' }}>My Subscriptions</h1>
          <Button
            style={{
              maxWidth: '200px',
              maxHeight: '48px',
              marginTop: 'auto',
              marginLeft: 'auto',
              marginBottom: '16px',
            }}
            onClick={refreshUserToken}
          >
            Refresh Token
          </Button>
        </div>
        <ReactTablev6
          minRows={(subscriptionData || []).length ? 0 : 10}
          columns={columns}
          data={subscriptionData}
          loading={loadingData}
          keyField="Resource Id"
        />
      </div>
    </>
  );
};

export default Subscription;
